import { useEffect, useRef } from "react";

import { useLocation } from "react-router-dom";

import { ChatConversation } from "components/ChatConversation";
import { LoadingStandAloneChat } from "components/LoadingStandAloneChat";
import { useChatUnreadContext } from "context/ChatUnreadContext";
import {
  useAuthContext,
  useChannelAPI,
  useShipmentPageContext,
  useUserChatInfo,
} from "hooks";
import { channelExistsInChatInfo } from "utils";

import { channelShipmentMatchesPathAndContext } from "./utils";

interface Props {
  channelName: string;
}

export function StandAloneLiveChat(props: Props) {
  const { channelName } = props;
  const { user, pubnub: pubnubUserInfo } = useAuthContext();
  const { chatInfo } = useUserChatInfo();
  const { shipment } = useShipmentPageContext();
  const { markChannelRead } = useChatUnreadContext();
  const { updateTimeTokenForChannel } = useChannelAPI();

  const location = useLocation();

  // Single-slot guard against useUserChatInfo's polling refresh re-firing the
  // effect every few seconds. We track only the most recently marked channel
  // so revisits (A → B → A) re-mark — without this, a badge bumped while the
  // user was on B would not clear when they navigated back to A.
  const lastMarkedChannelRef = useRef<string | null>(null);

  useEffect(() => {
    if (!chatInfo || !channelExistsInChatInfo(chatInfo, channelName)) return;
    if (lastMarkedChannelRef.current === channelName) return;
    lastMarkedChannelRef.current = channelName;
    markChannelRead(channelName);
    updateTimeTokenForChannel(channelName);
  }, [channelName, chatInfo, markChannelRead, updateTimeTokenForChannel]);

  if (
    pubnubUserInfo == null ||
    user == null ||
    chatInfo == null ||
    shipment === undefined ||
    !location
  ) {
    return <LoadingStandAloneChat />;
  }

  /* failsafe for ensuring the shipment id from the path, matches the shipment id
  from the context and matches shipment id from the channel passed
  */
  if (
    !channelShipmentMatchesPathAndContext(
      shipment,
      channelName,
      location.pathname,
    )
  ) {
    return <div />;
  }

  if (!channelExistsInChatInfo(chatInfo, channelName)) {
    return null;
  }

  return (
    <ChatConversation
      currentChannel={channelName}
      channelGroup={chatInfo?.user_channel_group}
      userPubnubData={pubnubUserInfo}
      useDesktopLayout={false}
      isStandaloneChat
    />
  );
}
